import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { getDateFnsLocale } from "../../utils/dateFnsLocale";
import styles from "./StudioDateInput.module.css";

export interface StudioDateInputProps {
    value?: string;
    onChange: (isoDate: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}

function parseIsoDate(value?: string): Date | null {
    if (!value) return null;
    const normalized = value.includes("T") ? value : `${value}T00:00:00`;
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
}

function toIsoDate(value: Date | null): string {
    if (!value || Number.isNaN(value.getTime())) return "";
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

const pickerTheme = createTheme({
    typography: {
        fontFamily: "var(--font-body, 'Readex Pro', sans-serif)",
    },
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    width: "100%",
                    minHeight: 46,
                    height: 46,
                    fontSize: 15,
                    fontFamily: "var(--font-body, 'Readex Pro', sans-serif)",
                    borderRadius: "var(--radius-sm, 7px)",
                    backgroundColor: "var(--surface)",
                    color: "var(--ink)",
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "var(--radius-sm, 7px)",
                        borderColor: "var(--line)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--line)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--green-500)",
                        borderWidth: 1,
                    },
                    "&.Mui-focused": {
                        boxShadow: "0 0 0 3px var(--green-100)",
                    },
                    "& input": {
                        padding: "0 14px",
                        fontSize: 15,
                        height: 46,
                        boxSizing: "border-box",
                    },
                    "& input::placeholder": {
                        color: "var(--ink-4)",
                        opacity: 1,
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    marginRight: 8,
                    color: "var(--ink-3)",
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    color: "var(--ink-3)",
                    fontSize: 20,
                },
            },
        },
    },
});

/** Studio date field — locale-aware display, stores ISO `yyyy-MM-dd`. */
const StudioDateInput: React.FC<StudioDateInputProps> = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    className = "",
}) => {
    const { i18n, t: rawT } = useTranslation();
    const t = rawT as any;
    const adapterLocale = useMemo(() => getDateFnsLocale(i18n.language), [i18n.language]);
    const parsed = useMemo(() => parseIsoDate(value), [value]);

    return (
        <div className={[styles.root, className].filter(Boolean).join(" ")}>
            <ThemeProvider theme={pickerTheme}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                    <DatePicker
                        value={parsed}
                        onChange={(next) => onChange(toIsoDate(next))}
                        disabled={disabled}
                        slotProps={{
                            textField: {
                                size: "small",
                                fullWidth: true,
                                placeholder: placeholder || t("selectDate") || "Select date",
                                inputProps: {
                                    "data-rw-control": "studio-date-input",
                                },
                            },
                            popper: {
                                className: "rw-studio-date-popper",
                                placement: "bottom-start",
                                sx: { zIndex: 11000 },
                            },
                        }}
                    />
                </LocalizationProvider>
            </ThemeProvider>
        </div>
    );
};

export default StudioDateInput;
